// src/components/GieokExplorer/GieokExplorer.jsx
// 기억의문화 카테고리 전용: 대분류(관) 탭 → 중분류/소분류 필터 → 판매중 NFT 목록
import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../../apiClient';
import { GridNFT } from '../GridNFT';

const PAGE_SIZE = 20;

const SORT_OPTIONS = [
  { id: 'Name', label: '위치순' },
  { id: 'Recent', label: '최신순' },
  { id: 'PriceAsc', label: '낮은 가격순' },
  { id: 'PriceDesc', label: '높은 가격순' },
];

const chipBase =
  'font-display flex h-8 items-center rounded-lg px-3 text-sm font-semibold transition-colors whitespace-nowrap';
const chipOn = `${chipBase} bg-accent text-white border border-transparent`;
const chipOff = `${chipBase} border border-jacarta-100 bg-white text-jacarta-500 hover:bg-accent hover:text-white hover:border-transparent dark:border-jacarta-600 dark:bg-jacarta-900 dark:text-white`;

// group2/group3 값 정렬: 숫자가 들어가면 숫자 기준(1인 < 3인 < 10인), 아니면 지정 순서
const GRADE_ORDER = ['기본', '고급', '최고급'];
const sortValues = (values) =>
  [...values].sort((a, b) => {
    const ga = GRADE_ORDER.indexOf(a);
    const gb = GRADE_ORDER.indexOf(b);
    if (ga !== -1 && gb !== -1) return ga - gb;
    const na = parseInt(a, 10);
    const nb = parseInt(b, 10);
    if (!isNaN(na) && !isNaN(nb) && na !== nb) return na - nb;
    return String(a).localeCompare(String(b), 'ko');
  });

/**
 * props
 *  - groups: [{group1, group2, group3, cnt}]  (상위에서 한 번 조회)
 *  - g1, g2, g3: 현재 선택값 (URL 쿼리)
 *  - onChange({g1, g2, g3}): 선택 변경
 */
const GieokExplorer = ({ groups, g1, g2, g3, onChange }) => {
  const [sorting, setSorting] = useState('Name');
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saleOnly, setSaleOnly] = useState(true);

  // 선택된 대분류 안에서 중분류/소분류 목록 + 개수
  const { level2, level3 } = useMemo(() => {
    const inG1 = groups.filter((g) => g.group1 === g1);
    const sum = (rows) => rows.reduce((s, r) => s + Number(r.cnt), 0);

    const l2 = sortValues([...new Set(inG1.map((g) => g.group2))]).map((v) => ({
      value: v,
      cnt: sum(inG1.filter((g) => g.group2 === v)),
    }));

    const inG2 = g2 ? inG1.filter((g) => g.group2 === g2) : inG1;
    const l3 = sortValues([...new Set(inG2.map((g) => g.group3).filter(Boolean))]).map((v) => ({
      value: v,
      cnt: sum(inG2.filter((g) => g.group3 === v)),
    }));

    return { level2: l2, level3: l3, };
  }, [groups, g1, g2]);

  // 필터·정렬이 바뀌면 1페이지부터 다시
  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get('blockchain/gieok/assets', {
          params: {
            group1: g1 || undefined,
            group2: g2 || undefined,
            group3: g3 || undefined,
            sorting,
            page: 1,
            itemsPerPage: PAGE_SIZE,
          },
        });
        if (ignore) return;
        setAssets(data.assets || []);
        setTotal(data.total || 0);
        setSaleOnly(data.saleOnly !== false);
        setPage(1);
      } catch (e) {
        if (!ignore) {
          console.error(e);
          setAssets([]);
          setTotal(0);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [g1, g2, g3, sorting]);

  const loadMore = async () => {
    const next = page + 1;
    setLoading(true);
    try {
      const { data } = await apiClient.get('blockchain/gieok/assets', {
        params: {
          group1: g1 || undefined,
          group2: g2 || undefined,
          group3: g3 || undefined,
          sorting,
          page: next,
          itemsPerPage: PAGE_SIZE,
        },
      });
      setAssets((prev) => [...prev, ...(data.assets || [])]);
      setPage(next);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* 중분류 */}
      {level2.length > 1 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="mr-1 w-14 text-sm font-semibold text-jacarta-400">구역</span>
          <button className={!g2 ? chipOn : chipOff} onClick={() => onChange({ g1, g2: null, g3: null })}>
            전체
          </button>
          {level2.map((l) => (
            <button
              key={l.value}
              className={g2 === l.value ? chipOn : chipOff}
              onClick={() => onChange({ g1, g2: l.value, g3: null })}
            >
              {l.value} <span className="ml-1 opacity-60">{l.cnt}</span>
            </button>
          ))}
        </div>
      )}

      {/* 소분류 (등급) */}
      {level3.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="mr-1 w-14 text-sm font-semibold text-jacarta-400">등급</span>
          <button className={!g3 ? chipOn : chipOff} onClick={() => onChange({ g1, g2, g3: null })}>
            전체
          </button>
          {level3.map((l) => (
            <button
              key={l.value}
              className={g3 === l.value ? chipOn : chipOff}
              onClick={() => onChange({ g1, g2, g3: l.value })}
            >
              {l.value} <span className="ml-1 opacity-60">{l.cnt}</span>
            </button>
          ))}
        </div>
      )}

      {/* 결과 수 + 정렬 */}
      <div className="mb-6 mt-6 flex items-center justify-between">
        <span className="text-sm text-jacarta-500 dark:text-jacarta-300">
          {saleOnly ? '판매중' : '증서'} <b className="text-jacarta-700 dark:text-white">{total.toLocaleString()}</b>건
        </span>
        <select
          value={sorting}
          onChange={(e) => setSorting(e.target.value)}
          className="rounded-lg border border-jacarta-100 bg-white px-3 py-2 text-sm dark:border-jacarta-600 dark:bg-jacarta-700 dark:text-white"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* NFT 목록 */}
      <div className="grid grid-cols-1 gap-[1.875rem] md:grid-cols-2 lg:grid-cols-4">
        {assets.map((item) => (
          <GridNFT
            key={item.id}
            item={item}
            id={item.id}
            hash={item.hash}
            itemName={item.name}
            imageUrl={item.imageUrl}
            price={item.price}
            favoriteFlag={item.isFavoriteYn === 'Y'}
            favoriteCount={item.favoriteCount}
            saleStatus={item.assetStatus === 'ON_SALE' ? 'ON_SALE' : undefined}
          />
        ))}
      </div>

      {!loading && assets.length === 0 && (
        <p className="py-20 text-center text-jacarta-400">{saleOnly ? '판매중인 증서가 없습니다.' : '증서가 없습니다.'}</p>
      )}

      {assets.length < total && (
        <div className="mt-10 text-center">
          <button
            disabled={loading}
            onClick={loadMore}
            className="rounded-full bg-accent px-8 py-3 text-center font-semibold text-white transition-all hover:bg-accent-dark disabled:opacity-50"
          >
            {loading ? '불러오는 중...' : `더 보기 (${assets.length} / ${total.toLocaleString()})`}
          </button>
        </div>
      )}
    </div>
  );
};

export { GieokExplorer };
